import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterFromToDate from '@components/toolbar/FilterFromToDate'
import FilterFromToWeek from '@components/toolbar/FilterFromToWeek'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import FilterKpi from '@components/toolbar/FilterKpi'
import { FilterMyList } from '@components/toolbar/FilterMyList'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterSelectString } from '@components/toolbar/FilterSelectString'
import type { FilterOption, GroupedFilterOption } from '@models/Filter'
import { defaultIsOptionEqualToValue } from '@util/filter'
import { filterVerantwoordelijkeOptionsByOrganisatie } from '@util/project-util'

import { useAuthenticatedUser } from '../../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../../core/hooks/useLocalFilters'
import {
  SubprocesStatusHoofdleiding,
  SubprocesTypesHoofdleiding,
} from '../../../entity/subprocessen/types/Subproces'
import { GROUPED_PAUSE_REDENEN } from '../../../entity/subprocessen/types/SubprocesRedenen'
import type { ProcesstapHoofdleiding } from '../../../status/types/ProcesstapHoofdleiding'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import { queryKey } from '../hooks/useSubprocessenSearch'
import type { SubprocessenSearchFilters as Filters } from '../types/SubprocessenSearch'

interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  filterOptions: {
    coordinerenden: string[]
    hoofdleiding: {
      gebieden: FilterOption[]
      netbeheerders: string[]
      gemeenten: string[]
      verantwoordelijken: GroupedFilterOption[]
      typeWerkTypes: string[]
    }
    labels: FilterOption[]
    processtappen: ProcesstapHoofdleiding[]
  }
}

const kpiTooltips = {
  red: 'Laat alleen de verlopen subprocessen zien',
  amber: 'Laat bijna verlopen subprocessen zien',
  green: 'Laat alle subprocessen zien die niet (bijna) verlopen zijn',
  grey: 'Laat alle subprocessen zien',
}

export const SubprocesFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  filterOptions: {
    coordinerenden,
    hoofdleiding: {
      gebieden,
      netbeheerders,
      gemeenten,
      verantwoordelijken,
      typeWerkTypes,
    },
    labels: labelOptions,
    processtappen,
  },
}: Props) => {
  const { combi, organisatie } = useAuthenticatedUser()

  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const {
    gebied,
    type,
    assignedTo,
    fromDate,
    toDate,
    fromChangedDate,
    toChangedDate,
    myList,
    startUitvoeringWeekVan,
    startUitvoeringWeekTot,
    gemeente,
    verantwoordelijke,
    typeWerk,
    status,
    processtatus,
    pauzeerReden,
    kpi,
    labels,
  } = localFilters

  const buildOption = (group) => (label) => ({ group, label })
  const assignees = (combi ? [buildOption('Combi')(combi)] : [])
    .concat(netbeheerders.map(buildOption('Netbeheerder')))
    .concat(coordinerenden.map(buildOption('Coordinerende')))

  const verantwoordelijkeOptions = filterVerantwoordelijkeOptionsByOrganisatie(
    verantwoordelijken,
    organisatie,
  )

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectObject
          options={Object.entries(SubprocesTypesHoofdleiding).map(
            ([key, val]) => ({
              code: key,
              label: val,
            }),
          )}
          label="Type subproces"
          filterKey="type"
          value={type}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={Object.entries(SubprocesStatusHoofdleiding).map(
            ([key, val]) => ({
              code: key,
              label: val,
            }),
          )}
          label="Status subproces"
          filterKey="status"
          value={status}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={GROUPED_PAUSE_REDENEN}
          label="Reden gepauzeerd"
          groupByProp="group"
          filterKey="pauzeerReden"
          value={pauzeerReden}
          onFilterChange={handleFilterChange}
          getOptionLabel={(x) => x.label}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={gebieden}
          label="Gebied"
          filterKey="gebied"
          value={gebied}
          onFilterChange={handleFilterChange}
          multiple
        />
      </FilterGridItem>

      {combi && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={assignees}
            label="Toegewezen aan"
            filterKey="assignedTo"
            value={assignedTo}
            onFilterChange={handleFilterChange}
          />
        </FilterGridItem>
      )}

      {labelOptions.length > 0 && (
        <FilterGridItem>
          <FilterSelectObject
            multiple
            options={labelOptions}
            label="Labels"
            filterKey="labels"
            value={labels}
            onFilterChange={handleFilterChange}
            isOptionEqualToValue={defaultIsOptionEqualToValue}
          />
        </FilterGridItem>
      )}

      <FilterGridItem>
        <FilterSelectObject
          options={verantwoordelijkeOptions}
          label="Projectverantwoordelijke"
          filterKey="verantwoordelijke"
          value={verantwoordelijke}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Opleverdatum"
          fromDate={fromDate}
          toDate={toDate}
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToDate
          onFilterChange={handleFilterChange}
          label="Wijzigingsdatum"
          fromDate={fromChangedDate}
          toDate={toChangedDate}
          prop="ChangedDate"
        />
      </FilterGridItem>

      <FilterGridItem minColumns="double">
        <FilterFromToWeek
          fromWeek={startUitvoeringWeekVan}
          toWeek={startUitvoeringWeekTot}
          onFilterChange={handleFilterChange}
          label="Project gepland"
          fromKey="startUitvoeringWeekVan"
          toKey="startUitvoeringWeekTot"
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={gemeente}
          options={gemeenten}
          label="Gemeente"
          filterKey="gemeente"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={typeWerk}
          options={typeWerkTypes}
          label="Type werk"
          filterKey="typeWerk"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectString
          multiple
          value={processtatus}
          options={processtappen}
          label="Processtap"
          filterKey="processtatus"
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <FilterKpi
          onFilterChange={handleFilterChange}
          kpi={kpi}
          tooltips={kpiTooltips}
        />
        <StyledIconButton
          icon={<CloseIcon />}
          title="Reset filters"
          onClick={handleReset}
        />
        <StyledIconButton
          icon={<SearchIcon />}
          title="Zoeken"
          onClick={handleZoeken}
        />

        <FilterMyList myList={myList} onFilterChange={handleFilterChange} />

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
